import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Space, Spin, Switch, Tag, message } from 'antd';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../components/NavigationGuard';

export default function PartnerPushSettings() {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [validation, setValidation] = useState(null);
  const [reload, setReload] = useState(0);
  const isAdmin = user?.role === 'super_admin';
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);
  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/partner-push').then(({ data }) => {
      if (!cancelled) { setSettings(data); form.setFieldsValue({ ...data, apiKey: '' }); }
    }).catch(err => { if (!cancelled) setError(err.response?.data?.message || '配置加载失败，请重试'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [form, isAdmin, reload]);
  if (!isAdmin) return <Alert type="error" showIcon message="仅超级管理员可以配置伙伴通推送" />;
  const act = async action => {
    try {
      const values = await form.validateFields();
      setBusy(action); setError(''); setValidation(null);
      if (action === 'save') {
        const { data } = await api.put('/partner-push', values);
        setSettings(data); form.setFieldsValue({ apiKey: '' });
        setDirty(false);
        message.success('伙伴通推送配置已保存');
      } else {
        const { data } = await api.post('/partner-push/validate', values);
        setValidation(data);
      }
    } catch (err) { if (!err.errorFields) setError(err.response?.data?.message || '操作失败，请重试'); }
    finally { setBusy(''); }
  };
  return <Spin spinning={loading}>
    <Alert type="info" showIcon message="岗位查询和推送使用独立的 Key" description="本系统保存伙伴通的接收结果，不跟踪伙伴通内部的微信发送结果。" style={{ marginBottom: 20 }} />
    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} action={!settings && <Button onClick={() => setReload(v => v + 1)}>重试</Button>} />}
    <Space wrap style={{ marginBottom: 20 }}>
      <Tag color={settings?.hasApiKey ? 'green' : 'default'}>{settings?.hasApiKey ? '已配置推送 Key' : '尚未配置推送 Key'}</Tag>
      <Tag color={settings?.enabled ? 'blue' : 'default'}>{settings?.enabled ? '自动提交已启用' : '自动提交已停用'}</Tag>
    </Space>
    <Form form={form} layout="vertical" initialValues={{ enabled: false, timeoutSeconds: 15, defaultVehicleNeeded: false }}
      disabled={loading || !settings || Boolean(busy)} onValuesChange={() => { setDirty(true); setValidation(null); }} onFinish={() => act('save')}>
      <Form.Item name="apiUrl" label="伙伴通推送接口地址" rules={[{ required: true, message: '请输入完整的推送接口地址' }, { type: 'url', message: '请输入有效的 HTTP/HTTPS 地址' }]}
        extra="填写完整地址，例如 https://hr.qingz.xyz/api/worktool-push/simple-submit。">
        <Input maxLength={1000} placeholder="https://你的伙伴通域名/api/worktool-push/simple-submit" />
      </Form.Item>
      <Form.Item name="apiKey" label="推送 Key" rules={settings?.hasApiKey ? [] : [{ required: true, whitespace: true, message: '请输入伙伴通推送 Key' }]}
        extra={settings?.hasApiKey ? '已保存的密钥不会回显，留空保留原 Key。' : '填写伙伴通允许推送的 API Key，不能使用岗位查询专用 Key。'}>
        <Input.Password autoComplete="new-password" maxLength={2000} />
      </Form.Item>
      <Form.Item name="timeoutSeconds" label="提交超时（秒）" rules={[{ required: true, message: '请输入提交超时' }]} extra="超时会记录为“接收结果待确认”，本系统不会自动重发。"><InputNumber min={3} max={60} precision={0} /></Form.Item>
      <Form.Item name="defaultVehicleNeeded" label="原文未填写车辆需求时，默认需要车辆" valuePropName="checked" extra="仅在报名原文缺少车辆需求时使用。每次应用默认值都会在记录中注明。"><Switch checkedChildren="需要" unCheckedChildren="不需要" /></Form.Item>
      <Form.Item name="enabled" label="启用自动提交到伙伴通" valuePropName="checked" extra="开启后符合采集规则的新消息自动提交。关闭后仍保存消息、识别和匹配结果；超级管理员可在推送记录中手动推送“未提交”的记录。"><Switch /></Form.Item>
      <Space wrap><Button type="primary" htmlType="submit" loading={busy === 'save'}>保存配置</Button>
        <Button onClick={() => act('validate')} loading={busy === 'validate'}>检查配置格式</Button></Space>
    </Form>
    {validation && <Alert type="success" showIcon message={validation.message} style={{ marginTop: 20 }} />}
  </Spin>;
}
